import { useState } from 'react';
import { SmilePlus, Smile, Meh, Frown, ThumbsDown, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FACES = [
  { score: 5, icon: SmilePlus, label: 'Definitely', color: 'text-emerald-400', bg: 'bg-emerald-400/15 border-emerald-400/30', activeBg: 'bg-emerald-400/25 border-emerald-400/50 shadow-[0_0_12px_rgba(52,211,153,0.15)]' },
  { score: 4, icon: Smile, label: 'Probably', color: 'text-oxidized-teal', bg: 'bg-oxidized-teal/15 border-oxidized-teal/30', activeBg: 'bg-oxidized-teal/25 border-oxidized-teal/50 shadow-[0_0_12px_rgba(71,139,137,0.15)]' },
  { score: 3, icon: Meh, label: 'Maybe', color: 'text-ug-gold', bg: 'bg-ug-gold/15 border-ug-gold/30', activeBg: 'bg-ug-gold/25 border-ug-gold/50 shadow-[0_0_12px_rgba(198,169,111,0.15)]' },
  { score: 2, icon: Frown, label: 'Unlikely', color: 'text-orange-400', bg: 'bg-orange-400/15 border-orange-400/30', activeBg: 'bg-orange-400/25 border-orange-400/50 shadow-[0_0_12px_rgba(251,146,60,0.15)]' },
  { score: 1, icon: ThumbsDown, label: 'No', color: 'text-ug-status-red', bg: 'bg-ug-status-red/15 border-ug-status-red/30', activeBg: 'bg-ug-status-red/25 border-ug-status-red/50 shadow-[0_0_12px_rgba(196,114,114,0.15)]' },
];

const REASONS = [
  { id: 'quality', label: 'Edit quality' },
  { id: 'turnaround', label: 'Turnaround time' },
  { id: 'communication', label: 'Communication' },
  { id: 'music', label: 'Music / sound' },
  { id: 'color', label: 'Color / grade' },
  { id: 'pacing', label: 'Pacing / flow' },
  { id: 'price', label: 'Price' },
  { id: 'other', label: 'Other' },
];

/**
 * Combined feedback survey shown when a client approves a project.
 * Captures: edit quality rating (stars) + likelihood to return (faces) + reasons + comment
 */
export default function SatisfactionSurvey({ onSubmit, onCancel, submitting }) {
  const [editRating, setEditRating] = useState(0);
  const [score, setScore] = useState(0);
  const [reasons, setReasons] = useState([]);
  const [comment, setComment] = useState('');

  const toggleReason = (id) => setReasons(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);

  const handleSubmit = () => {
    onSubmit({ score, edit_rating: editRating, reasons, comment });
  };

  return (
    <div className="space-y-5" data-testid="satisfaction-survey">
      {/* Edit quality stars */}
      <div>
        <h3 data-role="section-title" className="font-sans text-sm font-semibold text-zinc-100 mb-1">How would you rate this edit?</h3>
        <p className="font-sans text-[10px] text-zinc-500 mb-2">Rate the quality of the editing work itself.</p>
        <div className="flex items-center gap-1" data-testid="edit-rating-stars">
          {[1,2,3,4,5].map(s => (
            <button key={s} onClick={() => setEditRating(s)} data-testid={`edit-star-${s}`} className="transition-colors p-0.5">
              <Star className={`w-7 h-7 ${s <= editRating ? 'text-ug-gold fill-ug-gold' : 'text-zinc-700'}`} strokeWidth={1.5} />
            </button>
          ))}
          {editRating > 0 && <span className="font-sans text-xs text-zinc-400 ml-2">{editRating}/5</span>}
        </div>
      </div>

      {/* Likelihood to return — face scale */}
      <div>
        <h3 data-role="section-title" className="font-sans text-sm font-semibold text-zinc-100 mb-1">How likely are you to use us again?</h3>
        <p className="font-sans text-[10px] text-zinc-500 mb-2">This is separate from the edit quality — you might love the edit but have other reasons.</p>
      </div>
      <div className="flex gap-2 justify-center">
        {FACES.map(f => {
          const Icon = f.icon;
          const active = score === f.score;
          return (
            <button key={f.score} onClick={() => setScore(f.score)} data-testid={`satisfaction-score-${f.score}`}
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all ${active ? f.activeBg : f.bg} hover:scale-105`}>
              <Icon className={`w-6 h-6 ${active ? f.color : 'text-zinc-500'}`} strokeWidth={active ? 2 : 1.5} />
              <span className={`font-sans text-[9px] font-medium ${active ? f.color : 'text-zinc-500'}`}>{f.label}</span>
            </button>
          );
        })}
      </div>

      {/* Reasons — show when score is Maybe (3) or lower, or edit quality is poor */}
      {score > 0 && (score <= 3 || editRating <= 3) && (
        <div>
          <p className="font-sans text-[10px] font-medium text-zinc-400 mb-2">
            {score <= 2 || editRating <= 2 ? "What could we improve?" : "Anything we could do better?"}
            <span className="text-zinc-600 ml-1">(select all that apply)</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {REASONS.map(r => (
              <button key={r.id} onClick={() => toggleReason(r.id)} data-testid={`reason-${r.id}`}
                className={`px-3 py-1.5 rounded-lg font-sans text-[10px] font-medium border transition-colors ${
                  reasons.includes(r.id)
                    ? 'bg-ug-gold/15 border-ug-gold/40 text-ug-gold'
                    : 'bg-ug-raised border-ug-border text-zinc-400 hover:border-zinc-500'
                }`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Comment */}
      {(score > 0 || editRating > 0) && (
        <div>
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2}
            placeholder={(score <= 2 || editRating <= 2) ? "Tell us more — we want to make it right..." : "Any other feedback? (optional)"}
            className="w-full bg-ug-surface border border-ug-border text-zinc-200 rounded-xl p-3 text-xs placeholder:text-zinc-600 focus:border-ug-gold/40 outline-none resize-none"
            data-testid="satisfaction-comment" />
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button onClick={handleSubmit} disabled={!editRating || !score || submitting} data-testid="satisfaction-submit"
          className="flex-1 bg-oxidized-teal/80 hover:bg-oxidized-teal text-white font-sans text-xs font-medium rounded-xl">
          {submitting ? 'Approving...' : 'Approve Project'}
        </Button>
        <Button variant="outline" onClick={onCancel} className="border-ug-border text-zinc-500 font-sans text-xs rounded-xl" data-testid="satisfaction-cancel">
          Back
        </Button>
      </div>
    </div>
  );
}
